import type { FeedParseError, FeedParseResult } from '../core/parsePrimitives';
import { parseEchoErrorEnvelope } from './wire';

type EchoResponseParser<T> = (value: unknown, path?: string) => FeedParseResult<T>;

/** Failure channel for echo feed HTTP calls after the response body is available. */
export type EchoFeedApiErrorKind = 'api' | 'http' | 'parse';

/** Thrown for echo feed API envelopes, non-2xx HTTP statuses, and malformed JSON/parser payloads. */
export class EchoFeedApiError extends Error {
  readonly kind: EchoFeedApiErrorKind;
  readonly status?: number;
  readonly errors?: string[];
  readonly validations?: Record<string, string[]>;
  readonly parseError?: FeedParseError;

  constructor(
    message: string,
    details: {
      readonly kind: EchoFeedApiErrorKind;
      readonly status?: number;
      readonly errors?: string[];
      readonly validations?: Record<string, string[]>;
      readonly parseError?: FeedParseError;
    }
  ) {
    super(message);
    this.name = 'EchoFeedApiError';
    this.kind = details.kind;
    if (details.status !== undefined) this.status = details.status;
    if (details.errors !== undefined) this.errors = details.errors;
    if (details.validations !== undefined) this.validations = details.validations;
    if (details.parseError !== undefined) this.parseError = details.parseError;
  }
}

/** Reads a response body as JSON or throws a parse-kind error annotated at the root path. */
export async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new EchoFeedApiError('response body was not valid JSON', {
      kind: 'parse',
      status: response.status,
      parseError: {
        path: '$',
        message: 'expected JSON response body'
      }
    });
  }
}

/** Parses echo success envelopes, preserving API-envelope errors before HTTP and payload failures. */
export async function parseApiResponse<T>(
  response: Response,
  parseValue: EchoResponseParser<T>
): Promise<T> {
  const json = await readJson(response);
  const errorEnvelope = parseEchoErrorEnvelope(json);
  if (errorEnvelope.ok) {
    throw new EchoFeedApiError(errorEnvelope.value.errors[0] ?? 'API request failed', {
      kind: 'api',
      status: response.status,
      errors: errorEnvelope.value.errors,
      validations: errorEnvelope.value.validations
    });
  }

  const parsed = parseValue(json);
  if (!parsed.ok) {
    throw new EchoFeedApiError(`${parsed.error.path}: ${parsed.error.message}`, {
      kind: response.ok ? 'parse' : 'http',
      status: response.status,
      parseError: parsed.error
    });
  }

  if (!response.ok) {
    throw new EchoFeedApiError(`HTTP ${response.status}`, {
      kind: 'http',
      status: response.status
    });
  }

  return parsed.value;
}
